export type ContractTableAlignment = 'center' | 'left' | 'right';

export interface ContractRichTextBlock {
	id: string;
	html: string;
	markdown: string;
	type: 'rich-text';
}

export interface ContractTableBlock {
	id: string;
	alignments: ContractTableAlignment[];
	header: string[];
	rows: string[][];
	type: 'table';
}

export type ContractContentBlock = ContractRichTextBlock | ContractTableBlock;

const escapeHtml = (value: string): string =>
	value
		.replace(/&/gu, '&amp;')
		.replace(/</gu, '&lt;')
		.replace(/>/gu, '&gt;')
		.replace(/"/gu, '&quot;')
		.replace(/'/gu, '&#039;');

const renderInlineMarkdown = (value: string): string =>
	escapeHtml(value)
		.replace(
			/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/gu,
			'<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
		)
		.replace(/\*\*(.+?)\*\*/gu, '<strong>$1</strong>')
		.replace(/__(.+?)__/gu, '<strong>$1</strong>')
		.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/gu, '<em>$1</em>')
		.replace(/`([^`]+)`/gu, '<code>$1</code>');

export const markdownToHtml = (value: string): string => {
	const lines = value.trim().split(/\r?\n/u);
	const html: string[] = [];
	let listType: 'ol' | 'ul' | null = null;

	const closeList = (): void => {
		if (listType) {
			html.push(`</${listType}>`);
			listType = null;
		}
	};

	for (const line of lines) {
		const unordered = line.match(/^\s*[-*+]\s+(.+)$/u);
		const ordered = line.match(/^\s*\d+[.)]\s+(.+)$/u);

		if (unordered || ordered) {
			const nextListType = ordered ? 'ol' : 'ul';

			if (listType !== nextListType) {
				closeList();
				listType = nextListType;
				html.push(`<${listType}>`);
			}

			html.push(
				`<li>${renderInlineMarkdown((unordered ?? ordered)?.[1] ?? '')}</li>`,
			);
		} else {
			closeList();

			if (!line.trim()) {
				html.push('<p><br></p>');
			} else {
				const quote = line.match(/^\s*>\s?(.*)$/u);

				html.push(
					quote
						? `<blockquote>${renderInlineMarkdown(quote[1])}</blockquote>`
						: `<p>${renderInlineMarkdown(line)}</p>`,
				);
			}
		}
	}

	closeList();

	return html.join('');
};

function renderNodeAsMarkdown(node: Node): string {
	if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
	if (node.nodeType !== Node.ELEMENT_NODE) return '';

	const element = node as Element;
	const children = Array.from(element.childNodes)
		.map(renderNodeAsMarkdown)
		.join('');

	switch (element.tagName.toLowerCase()) {
		case 'a':
			return `[${children}](${element.getAttribute('href') ?? ''})`;
		case 'b':
		case 'strong':
			return `**${children}**`;
		case 'blockquote':
			return `> ${children.trim()}\n`;
		case 'br':
			return '\n';
		case 'code':
			return `\`${children}\``;
		case 'em':
		case 'i':
			return `*${children}*`;
		case 'li':
			return children;
		case 'ol':
			// eslint-disable-next-line @typescript-eslint/no-use-before-define
			return `${renderListAsMarkdown(element, true)}\n`;
		case 'p':
			return `${children.replace(/\s+$/u, '')}\n`;
		case 's':
		case 'strike':
			return `~~${children}~~`;
		case 'u':
			return `<u>${children}</u>`;
		case 'ul':
			// eslint-disable-next-line @typescript-eslint/no-use-before-define
			return `${renderListAsMarkdown(element, false)}\n`;
		default:
			return children;
	}
}

function renderListAsMarkdown(element: Element, isOrdered: boolean): string {
	return Array.from(element.children)
		.filter((child) => child.tagName.toLowerCase() === 'li')
		.map((child, index) => {
			const isBullet = child.getAttribute('data-list') === 'bullet';
			const marker = isOrdered && !isBullet ? `${index + 1}.` : '-';
			const content = Array.from(child.childNodes)
				.map(renderNodeAsMarkdown)
				.join('')
				.trim();

			return `${marker} ${content}`;
		})
		.join('\n');
}

export const htmlToMarkdown = (value: string): string => {
	const container = document.createElement('div');
	container.innerHTML = value;

	return Array.from(container.childNodes)
		.map(renderNodeAsMarkdown)
		.join('')
		.replace(/\n{3,}/gu, '\n\n')
		.trim();
};

const splitTableRow = (line: string): string[] =>
	line
		.trim()
		.replace(/^\|/u, '')
		.replace(/\|$/u, '')
		.split('|')
		.map((cell) => cell.trim());

const isTableDivider = (line: string): boolean => {
	const cells = splitTableRow(line);

	return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/u.test(cell));
};

const isTableRow = (line: string): boolean => /^\s*\|.*\|\s*$/u.test(line);

const getAlignment = (value: string): ContractTableAlignment => {
	if (value.startsWith(':') && value.endsWith(':')) return 'center';
	if (value.endsWith(':')) return 'right';

	return 'left';
};

const createRichTextBlock = (
	lines: string[],
	index: number,
): ContractRichTextBlock | null => {
	const markdown = lines.join('\n').trim();

	return markdown
		? {
				id: `content-block-${index}`,
				html: markdownToHtml(markdown),
				markdown,
				type: 'rich-text',
			}
		: null;
};

export const parseContractContentBlocks = (
	value: string,
): ContractContentBlock[] => {
	const lines = value.split(/\r?\n/u);
	const blocks: ContractContentBlock[] = [];
	let richLines: string[] = [];
	let lineIndex = 0;

	const appendRichText = (): void => {
		const block = createRichTextBlock(richLines, blocks.length);

		if (block) blocks.push(block);
		richLines = [];
	};

	while (lineIndex < lines.length) {
		const hasTable =
			isTableRow(lines[lineIndex]) &&
			isTableDivider(lines[lineIndex + 1] ?? '');

		if (hasTable) {
			appendRichText();
			const header = splitTableRow(lines[lineIndex]);
			const alignments = splitTableRow(lines[lineIndex + 1]).map(getAlignment);
			const rows: string[][] = [];
			lineIndex += 2;

			while (lineIndex < lines.length && isTableRow(lines[lineIndex])) {
				rows.push(splitTableRow(lines[lineIndex]));
				lineIndex += 1;
			}

			blocks.push({
				id: `content-block-${blocks.length}`,
				alignments,
				header,
				rows,
				type: 'table',
			});
		} else {
			richLines.push(lines[lineIndex]);
			lineIndex += 1;
		}
	}

	appendRichText();

	return blocks.length > 0
		? blocks
		: [{ id: 'content-block-0', html: '', markdown: '', type: 'rich-text' }];
};

const serializeTable = (block: ContractTableBlock): string => {
	const alignment = block.alignments.map((item) => {
		if (item === 'center') return ':---:';
		if (item === 'right') return '---:';

		return '---';
	});
	const rows = [block.header, alignment, ...block.rows];

	return rows.map((row) => `| ${row.join(' | ')} |`).join('\n');
};

export const serializeContractContentBlocks = (
	blocks: ContractContentBlock[],
): string =>
	blocks
		.map((block) =>
			block.type === 'table' ? serializeTable(block) : block.markdown,
		)
		.join('\n\n')
		.trim();
